import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

const articleLinks = [
  { name: 'Sugargoo Review 2026', to: '/article/sugargoo-review' },
  { name: 'Shipping Cost Guide', to: '/article/sugargoo-shipping-cost-guide' },
  { name: 'Coupon & Discount Guide', to: '/article/sugargoo-coupon-guide' },
  { name: 'Complete Guide 2026', to: '/article/sugargoo-spreadsheet-complete-guide' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const closeAll = () => {
    setMobileOpen(false);
    setMobileCatOpen(false);
    setMobileGuideOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/80 backdrop-blur-md">
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2 no-underline group">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 group-hover:text-primary-500 transition-colors whitespace-nowrap m-0">
              Sugargoo Spreadsheet
            </h1>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Home
            </Link>
            <a href="/#categories" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Categories
            </a>
            <a href="/#articles" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Articles
            </a>
            <a href="/#about" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              About
            </a>

            <div ref={guideRef} className="relative">
              <button
                onClick={() => setGuideOpen(!guideOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Shopping Guides
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-sm py-2 z-50">
                  {articleLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setGuideOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Visit Main Site
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col gap-1 px-2">
              <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors py-2.5 px-3 cursor-pointer rounded-lg hover:bg-background-100" onClick={closeAll}>
                <i className="ri-home-4-line mr-2"></i>Home
              </Link>

              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors py-2.5 px-3 cursor-pointer rounded-lg hover:bg-background-100 w-full"
              >
                <span><i className="ri-grid-line mr-2"></i>Categories</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="ml-4 pl-3 border-l-2 border-background-200/70 mb-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeAll}
                      className="flex items-center gap-2 text-xs text-foreground-600 hover:text-primary-500 transition-colors py-2 px-2 cursor-pointer rounded-lg hover:bg-background-100"
                    >
                      <i className={`${cat.icon} text-sm w-5 text-center`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <button
                onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
                className="flex items-center justify-between text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors py-2.5 px-3 cursor-pointer rounded-lg hover:bg-background-100 w-full"
              >
                <span><i className="ri-book-open-line mr-2"></i>Shopping Guides</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${mobileGuideOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileGuideOpen && (
                <div className="ml-4 pl-3 border-l-2 border-background-200/70 mb-1">
                  {articleLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={closeAll}
                      className="block text-xs text-foreground-600 hover:text-primary-500 transition-colors py-2 px-2 cursor-pointer rounded-lg hover:bg-background-100"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}

              <a href="/#articles" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors py-2.5 px-3 cursor-pointer rounded-lg hover:bg-background-100" onClick={closeAll}>
                <i className="ri-article-line mr-2"></i>Articles
              </a>
              <a href="/#about" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors py-2.5 px-3 cursor-pointer rounded-lg hover:bg-background-100" onClick={closeAll}>
                <i className="ri-information-line mr-2"></i>About
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer mt-2"
              >
                Visit Main Site
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}